import { useState, useEffect } from "react";
import BasicDropdown from "../../components/ui/forms/dropdown/BasicDropdown";
import { getAllUsers, updateUserRole } from "../../services/usersAPI";

export const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllUsers();
      setUsers(response.data);
    } catch (err) {
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const options = users.map((u) => ({
    value: u.UserID,
    label: `${u.firstName} ${u.lastName} (${u.username})`,
  }));

  const selectedUser = selected ? users.find((u) => u.UserID === selected.value) : null;

  const handleChange = (option) => {
    setSelected(option);
    setSaved(false);
    setSaving(false);
  };

  const handleToggleAdmin = async () => {
    if (!selectedUser || saving) return;

    const newRole = selectedUser.role === "admin" ? "user" : "admin";

    setSaving(true);
    setSaved(false);

    try {
      await updateUserRole(selectedUser.UserID, newRole);
      setUsers((prev) =>
        prev.map((u) =>
          u.UserID === selectedUser.UserID ? { ...u, role: newRole } : u
        )
      );
      setSaving(false);
      setSaved(true);

      setTimeout(() => {
        setSaved(false);
      }, 2000);
    } catch (err) {
      setSaving(false);
      setError("Failed to update role.");
    }
  };

  return (
    <div className="bg-flyers-black min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-md mx-auto space-y-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-wide text-flyers-orange text-center">
          Admin Dashboard
        </h1>

        <div className="bg-flyers-charcoal border border-flyers-dark-gray p-6 rounded-lg">
          <label className="label mb-2">Select a User</label>

          {loading ? (
            <div className="flex items-center gap-2 py-3">
              <svg
                className="animate-spin h-5 w-5 text-flyers-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm text-flyers-mid-gray">Loading users...</span>
            </div>
          ) : error && users.length === 0 ? (
            <div className="py-3">
              <p className="text-sm text-red-400 mb-2">{error}</p>
              <button
                onClick={fetchUsers}
                className="text-sm text-flyers-orange hover:text-flyers-orange-light font-medium"
              >
                Retry
              </button>
            </div>
          ) : (
            <BasicDropdown
              name="user-select"
              options={options}
              placeholder="Select a user..."
              handleChange={handleChange}
              isSearchable={true}
              value={selected}
            />
          )}
        </div>

        {error && users.length > 0 && (
          <p className="text-sm text-red-400">{error}</p>
        )}

        {selectedUser && (
          <div className="bg-flyers-charcoal border border-flyers-dark-gray p-6 rounded-lg space-y-4">
            <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-flyers-orange">
              User Details
            </h2>

            {["firstName", "lastName", "email", "username"].map((field) => (
              <div key={field}>
                <label className="label mb-1">{field === "firstName" ? "First Name" : field === "lastName" ? "Last Name" : field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <input
                  type="text"
                  value={selectedUser[field]}
                  disabled
                  className="w-full px-3 py-2 border border-flyers-dark-gray rounded bg-flyers-black text-flyers-mid-gray cursor-not-allowed"
                />
              </div>
            ))}

            <div className="flex items-center justify-between pt-2">
              <label className="label mb-0">Admin</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleToggleAdmin}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                    saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } ${selectedUser.role === "admin" ? "bg-flyers-orange" : "bg-flyers-dark-gray"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${
                      selectedUser.role === "admin" ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                {saving && (
                  <span className="text-sm text-flyers-orange font-medium">Saving...</span>
                )}
                {saved && (
                  <span className="text-sm text-green-500 font-medium animate-pulse">Saved</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
